import { API_BASE_URL } from '../config';
import React, { useEffect, useState } from 'react';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const inputStyle = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' };

function emptyForm(catalog) {
  const source = catalog[0];
  return {
    NAME: '',
    SOURCE: source ? source.key : '',
    FIELDS: source ? source.fields.map((field) => field.key) : [],
    FREQUENCY: 'None',
    WEEKDAY: 1,
    MONTH_DAY: 1,
    TIME_OF_DAY: '08:00'
  };
}

function scheduleLabel(report) {
  if (!report.FREQUENCY || report.FREQUENCY === 'None') return 'Not scheduled';
  if (report.FREQUENCY === 'Daily') return `Daily at ${report.TIME_OF_DAY} IST`;
  if (report.FREQUENCY === 'Weekly') return `Weekly on ${WEEKDAYS[report.WEEKDAY] || 'Monday'} at ${report.TIME_OF_DAY} IST`;
  return `Monthly on day ${report.MONTH_DAY} at ${report.TIME_OF_DAY} IST`;
}

const ReportsSection = () => {
  const [catalog, setCatalog] = useState([]);
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const headers = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  };

  const load = async () => {
    const auth = headers();
    if (!auth) return;
    const [catalogResponse, reportsResponse] = await Promise.all([
      fetch(`${API_BASE_URL}/reports/catalog`, { headers: auth }),
      fetch(`${API_BASE_URL}/reports`, { headers: auth })
    ]);
    const catalogData = await catalogResponse.json();
    const reportData = await reportsResponse.json();
    if (!catalogResponse.ok) throw new Error(catalogData.message || 'Could not load report sources');
    if (!reportsResponse.ok) throw new Error(reportData.message || 'Could not load reports');
    setCatalog(catalogData);
    setReports(reportData);
  };

  useEffect(() => {
    load().catch((err) => setError(err.message));
  }, []);

  const sourceOf = (key) => catalog.find((source) => source.key === key);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm(catalog));
    setError('');
    setNotice('');
  };

  const openEdit = (report) => {
    setEditingId(report._id);
    setForm({
      NAME: report.NAME,
      SOURCE: report.SOURCE,
      FIELDS: report.FIELDS || [],
      FREQUENCY: report.FREQUENCY || 'None',
      WEEKDAY: report.WEEKDAY ?? 1,
      MONTH_DAY: report.MONTH_DAY || 1,
      TIME_OF_DAY: report.TIME_OF_DAY || '08:00'
    });
    setError('');
    setNotice('');
  };

  const changeSource = (sourceKey) => {
    const source = sourceOf(sourceKey);
    setForm((current) => ({
      ...current,
      SOURCE: sourceKey,
      FIELDS: source ? source.fields.map((field) => field.key) : []
    }));
  };

  const toggleField = (key) => {
    setForm((current) => {
      const selected = new Set(current.FIELDS);
      if (selected.has(key)) selected.delete(key);
      else selected.add(key);
      return { ...current, FIELDS: Array.from(selected) };
    });
  };

  const save = async () => {
    const auth = headers();
    if (!auth || !form) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_BASE_URL}/reports${editingId ? `/${editingId}` : ''}`, {
        method: editingId ? 'PUT' : 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not save the report');
      setForm(null);
      setEditingId(null);
      setNotice('Report saved.');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (report) => {
    if (!window.confirm(`Delete ${report.NAME}?`)) return;
    const auth = headers();
    const response = await fetch(`${API_BASE_URL}/reports/${report._id}`, { method: 'DELETE', headers: auth });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(data.message || 'Could not delete the report');
      return;
    }
    setNotice('Report deleted.');
    load().catch((err) => setError(err.message));
  };

  const download = async (report, scheduled) => {
    const auth = headers();
    setError('');
    const range = !scheduled && (startDate || endDate)
      ? `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`
      : '';
    const path = scheduled ? 'scheduled-file' : `download${range}`;
    const response = await fetch(`${API_BASE_URL}/reports/${report._id}/${path}`, { headers: auth });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      setError(data.message || 'Could not download the report');
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report.NAME}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const selectedSource = form ? sourceOf(form.SOURCE) : null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'end', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <label>From
            <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} style={inputStyle} />
          </label>
          <label>To
            <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} style={inputStyle} />
          </label>
        </div>
        <button type="button" onClick={openNew} style={{ padding: '8px 14px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          New report
        </button>
      </div>
      <p style={{ marginTop: 0, color: '#6c757d', fontSize: '0.9rem' }}>Leave the dates empty to include every record. The dates apply to Excel downloads for reports that have a date.</p>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {notice && <p style={{ color: '#155724' }}>{notice}</p>}

      {form && selectedSource && (
        <div style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff' }}>
          <h4 style={{ marginTop: 0 }}>{editingId ? 'Edit report' : 'New report'}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
            <label>Name
              <input value={form.NAME} onChange={(event) => setForm({ ...form, NAME: event.target.value })} style={inputStyle} />
            </label>
            <label>Source
              <select value={form.SOURCE} onChange={(event) => changeSource(event.target.value)} disabled={Boolean(editingId && reports.find((item) => item._id === editingId && item.IS_BUILTIN))} style={inputStyle}>
                {catalog.map((source) => <option key={source.key} value={source.key}>{source.label}</option>)}
              </select>
            </label>
            <label>Frequency
              <select value={form.FREQUENCY} onChange={(event) => setForm({ ...form, FREQUENCY: event.target.value })} style={inputStyle}>
                <option value="None">Not scheduled</option>
                <option value="Daily">Daily</option>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
              </select>
            </label>
            <label>Time (IST)
              <input type="time" value={form.TIME_OF_DAY} onChange={(event) => setForm({ ...form, TIME_OF_DAY: event.target.value })} style={inputStyle} />
            </label>
            {form.FREQUENCY === 'Weekly' && (
              <label>Weekday
                <select value={form.WEEKDAY} onChange={(event) => setForm({ ...form, WEEKDAY: Number(event.target.value) })} style={inputStyle}>
                  {WEEKDAYS.map((day, index) => <option key={day} value={index}>{day}</option>)}
                </select>
              </label>
            )}
            {form.FREQUENCY === 'Monthly' && (
              <label>Day of month
                <input type="number" min="1" max="28" value={form.MONTH_DAY} onChange={(event) => setForm({ ...form, MONTH_DAY: Number(event.target.value) })} style={inputStyle} />
              </label>
            )}
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <strong>Fields</strong>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', marginTop: '0.5rem' }}>
              {selectedSource.fields.map((field) => (
                <label key={field.key} style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                  <input type="checkbox" checked={form.FIELDS.includes(field.key)} onChange={() => toggleField(field.key)} />
                  {field.label}
                </label>
              ))}
            </div>
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={save} disabled={busy} style={{ padding: '8px 14px', backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              {busy ? 'Saving...' : 'Save report'}
            </button>
            <button type="button" onClick={() => setForm(null)} style={{ padding: '8px 14px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Report', 'Source', 'Fields', 'Schedule', 'Last scheduled run', ''].map((header) => (
                <th key={header} style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', backgroundColor: '#f8f9fa' }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reports.map((report) => (
              <tr key={report._id}>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{report.NAME}{report.IS_BUILTIN ? '' : ' (custom)'}</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{(sourceOf(report.SOURCE) || {}).label || report.SOURCE}</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{(report.FIELDS || []).length}</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{scheduleLabel(report)}</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{report.LAST_RUN_AT ? new Date(report.LAST_RUN_AT).toLocaleString() : 'Not run'}</td>
                <td style={{ padding: '8px', border: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                  <button type="button" onClick={() => download(report, false)} style={{ marginRight: '0.35rem', padding: '6px 8px', backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Excel</button>
                  <button type="button" onClick={() => openEdit(report)} style={{ marginRight: '0.35rem', padding: '6px 8px', backgroundColor: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Fields</button>
                  {report.HAS_SCHEDULED_FILE && (
                    <button type="button" onClick={() => download(report, true)} style={{ marginRight: '0.35rem', padding: '6px 8px', backgroundColor: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Last file</button>
                  )}
                  {!report.IS_BUILTIN && (
                    <button type="button" onClick={() => remove(report)} style={{ padding: '6px 8px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReportsSection;
